/**
 * tracking.js — DSGVO-konformer Event-Tracker
 *
 * Sendet Events an unser eigenes Backend (`/api/track-event`).
 * Keine Drittanbieter-Calls. Keine PII. Keine Cookies.
 *
 * Nutzung:
 *     import { trackEvent } from '../utils/tracking';
 *     trackEvent('whatsapp_click', { source: 'floating_whatsapp_button' });
 *
 * Die Funktion blockiert NIE — bei navigation-during-click (z. B. <a target=_blank>)
 * verwendet sie `navigator.sendBeacon`, sodass das Event garantiert ankommt
 * bevor der Browser-Tab das DOM verlässt.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ENDPOINT = `${BACKEND}/api/track-event`;

// ── Helpers ───────────────────────────────────────────────────────────────
function _detectDevice() {
  if (typeof window === 'undefined') return 'unknown';
  const w = window.innerWidth || 0;
  if (w === 0) return 'unknown';
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function _readUtmParams() {
  if (typeof window === 'undefined') return {};
  try {
    const sp = new URLSearchParams(window.location.search);
    const utm = {};
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
      const v = sp.get(key);
      if (v) utm[key] = v;
    }
    return utm;
  } catch (_e) {
    return {};
  }
}

// ── Public API ────────────────────────────────────────────────────────────
/**
 * Schickt ein Tracking-Event an unser eigenes Backend.
 *
 * @param {string} event   z. B. 'whatsapp_click'
 * @param {object} props   { source, meta, ...optional overrides }
 */
export function trackEvent(event, props = {}) {
  if (!event || typeof window === 'undefined') return;

  const payload = {
    event,
    source: props.source || 'unknown',
    page_url: window.location.href,
    page_path: window.location.pathname,
    device_type: props.device_type || _detectDevice(),
    referrer: document.referrer || null,
    ..._readUtmParams(),
    ...(props.meta ? { meta: props.meta } : {}),
  };

  // sendBeacon ist der ZUVERLÄSSIGE Weg, auch wenn der User gleich die Tab
  // verlässt (z. B. WhatsApp-Link öffnet neue Seite). Fallback: fetch keepalive.
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
  } catch (_e) { /* swallow, fall through to fetch */ }

  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => { /* best-effort, NIE blockieren */ });
  } catch (_e) { /* swallow */ }
}
